/*
 * cross-sell-popup
 * ------------------------------------------------------------
 * Custom element rendered once on the PDP. Implements the
 * cart-notification interface (getSectionsToRender / setActiveElement /
 * renderContents) so <product-form> uses it as its cart target instead
 * of <cart-notification> / <cart-drawer>.
 *
 * Algorithm on PDP add-to-cart:
 *   1. <product-form> POSTs /cart/add with sections we ask for (cart bubble).
 *   2. It then calls our renderContents(response).
 *   3. We refresh the cart-icon bubble, fetch /cart.js to know what's in cart,
 *      then compute eligible cards = AVAILABLE && NOT already in cart && not
 *      the current PDP product.
 *   4. If 0 eligible cards: don't open the popup; open <cart-drawer> instead
 *      (or navigate to /cart if no drawer exists).
 *   5. If >0 eligible: hide ineligible ones and open the dialog.
 *
 * Inner card adds use the SAME cross-sell-popup instance as their cart
 * target. After a successful inner add we keep the popup open, show
 * "Added to cart" on that card's button, then hide ineligible cards. If no
 * cards remain, we close and open the cart drawer (or go to /cart).
 *
 * Closing rules (set via UI handlers):
 *   - Close button (×)              -> close only
 *   - Backdrop click                -> close only
 *   - "No thanks, continue shopping"-> close only
 *   - "View Cart →"                 -> close + open cart-drawer
 *   - Successful add from a cross-sell card  -> button shows "Added to cart";
 *     card may hide if no longer eligible; popup closes if no cards left
 */

const CS_LOG = (...args) => console.log('[cross-sell-popup]', ...args);

class CrossSellPopup extends HTMLElement {
  constructor() {
    super();
    CS_LOG('constructor');

    this.dialog = this.querySelector('dialog');
    this.cards = Array.from(this.querySelectorAll('.cs-card'));
    CS_LOG('found', this.cards.length, 'cards');

    // Server-rendered initial cart state (avoids a fetch on first render).
    let initialIds = [];
    try {
      initialIds = JSON.parse(this.dataset.initialCartVariantIds || '[]');
    } catch (e) {
      CS_LOG('failed to parse initial-cart-variant-ids', e);
    }
    this.cartVariantIds = new Set(initialIds.map((id) => parseInt(id, 10)));
    CS_LOG('initial cart variants', Array.from(this.cartVariantIds));

    this.cards.forEach((card) => this.initCard(card));
    this.attachUiHandlers();

    // Expose a console hook so the popup can be opened manually for QA:
    //   > openCrossSellPopup()
    // It bypasses eligibility filtering (shows every card so layout/styles
    // can be inspected even if all variants are already in cart).
    window.openCrossSellPopup = () => {
      CS_LOG('manual open via window.openCrossSellPopup()');
      this.cards.forEach((card) => { card.hidden = false; });
      this.open();
    };
  }

  /* ---------- UI handlers ---------- */

  attachUiHandlers() {
    const closeBtn = this.querySelector('.cs-popup__close');
    if (closeBtn) {
      closeBtn.addEventListener('click', () => {
        CS_LOG('close button click');
        this.close();
      });
    }

    const noThanks = this.querySelector('.cs-popup__no-thanks');
    if (noThanks) {
      noThanks.addEventListener('click', () => {
        CS_LOG('no-thanks click');
        this.close();
      });
    }

    const viewCart = this.querySelector('.cs-popup__view-cart');
    if (viewCart) {
      viewCart.addEventListener('click', (e) => {
        e.preventDefault();
        CS_LOG('view-cart click');
        this.closeAndOpenCart();
      });
    }

    if (this.dialog) {
      this.dialog.addEventListener('click', (e) => {
        if (e.target === this.dialog) {
          CS_LOG('backdrop click');
          this.close();
        }
      });
      this.dialog.addEventListener('close', () => {
        document.body.classList.remove('overflow-hidden');
      });
    }
  }

  /* ---------- Variant pickers ---------- */

  initCard(card) {
    const dataEl = card.querySelector('script.cs-card__variants-data');
    if (!dataEl) {
      CS_LOG('no variants data on card', card);
      return;
    }
    let variants;
    try {
      variants = JSON.parse(dataEl.textContent);
    } catch (e) {
      CS_LOG('variants JSON parse failed', e, card);
      return;
    }
    if (!Array.isArray(variants) || variants.length === 0) return;

    card.__variants = variants;
    const initialId = parseInt(card.querySelector('input[name="id"]')?.value, 10);
    const initial = variants.find((v) => v.id === initialId) || variants[0];
    card.__selectedOptions = Array.isArray(initial.options) ? initial.options.slice() : [];

    CS_LOG('init card', { productId: card.dataset.productId, variants: variants.length, initial: card.__selectedOptions });

    CrossSellPopup.removeSingleValueSecurityLevelOptionRows(card);

    card.querySelectorAll('.cs-card__option-buttons').forEach((group) => {
      const position = parseInt(group.dataset.optionPosition, 10);
      group.addEventListener('click', (e) => {
        const btn = e.target.closest('.cs-card__option-btn');
        if (!btn) return;
        e.preventDefault();
        if (Number.isNaN(position)) return;

        CS_LOG('option click', { productId: card.dataset.productId, position, value: btn.dataset.optionValue });

        card.__selectedOptions[position] = btn.dataset.optionValue;
        group.querySelectorAll('.cs-card__option-btn').forEach((b) => b.classList.remove('is-active'));
        btn.classList.add('is-active');
        this.updateCardVariant(card);
      });
    });

    this.updateCardVariant(card);
  }

  updateCardVariant(card) {
    const variants = card.__variants || [];
    const selected = card.__selectedOptions || [];
    const match = variants.find(
      (v) => Array.isArray(v.options) && v.options.length === selected.length && v.options.every((o, i) => o === selected[i])
    );
    CS_LOG('updateCardVariant', { selected, matchId: match?.id, available: match?.available });
    if (!match) return;

    const idInput = card.querySelector('input[name="id"]');
    if (idInput) idInput.value = match.id;

    const priceEl = card.querySelector('.cs-card__price');
    if (priceEl && match.price) priceEl.textContent = match.price;

    const submitBtn = card.querySelector('button[type="submit"]');
    if (submitBtn) {
      submitBtn.disabled = !match.available;
      const submitText = submitBtn.querySelector('.cs-card__submit-text');
      if (submitText) {
        submitText.textContent = match.available
          ? CrossSellPopup.crossSellAddToCartLabel()
          : (window.variantStrings?.soldOut || 'Sold out');
      }
    }
  }

  /**
   * Cross-sell cards show price in `.cs-card__price`. Prefer `addToCartNoPrice` from
   * theme.liquid (`t` with empty price); fall back to stripping placeholders from `addToCart`.
   */
  static crossSellAddToCartLabel() {
    const noPrice = window.variantStrings?.addToCartNoPrice?.trim();
    if (noPrice) return noPrice;

    const raw = window.variantStrings?.addToCart || 'Add to Cart';
    return raw
      .replace(/\{\{\s*price\s*\}\}/gi, '')
      .replace(/\[\s*price\s*\]/gi, '')
      .replace(/\s+/g, ' ')
      .trim();
  }

  /**
   * Labels that mean “Security level” (EN/FR), compared after Unicode normalization.
   * Removes rows where Liquid could not hide them (e.g. NFD vs NFC accents).
   */
  static normalizeSecurityOptionLabel(text) {
    return String(text || '')
      .normalize('NFD')
      .replace(/\p{M}/gu, '')
      .replace(/\s+/g, ' ')
      .trim()
      .toLowerCase();
  }

  static removeSingleValueSecurityLevelOptionRows(card) {
    const targets = new Set(['security level', 'niveau de securite']);
    card.querySelectorAll('.cs-card__option').forEach((row) => {
      const pills = row.querySelectorAll('.cs-card__option-btn');
      if (pills.length !== 1) return;
      const labelEl = row.querySelector('.cs-card__option-label');
      const key = CrossSellPopup.normalizeSecurityOptionLabel(labelEl?.textContent);
      if (targets.has(key)) row.remove();
    });
  }

  /* ---------- cart-notification compatible API (used by <product-form>) ---------- */

  getSectionsToRender() {
    // We only need to keep the cart bubble accurate. The drawer and notification
    // sections may not exist on this page (cart_type may be "page").
    const sections = [];
    if (document.getElementById('cart-icon-bubble')) sections.push({ id: 'cart-icon-bubble' });
    return sections;
  }

  setActiveElement(element) {
    this.activeElement = element;
  }

  async renderContents(parsedState) {
    CS_LOG('renderContents called', { dialogOpen: this.dialog?.open, hasSections: !!parsedState?.sections });

    // 1) Refresh cart bubble.
    this.cartItemKey = parsedState?.key;
    // Remember the just-added product title for the drawer's green "added" notice.
    if (parsedState?.product_title) this.lastAddedTitle = parsedState.product_title;
    this.getSectionsToRender().forEach((section) => {
      const target = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      const html = parsedState?.sections?.[section.id];
      if (!target || !html) return;
      const inner = this.getSectionInnerHTML(html, section.selector);
      if (inner != null) target.innerHTML = inner;
    });

    // 2) Refresh local cart state from /cart.js (source of truth for variant ids).
    await this.refreshCartState();

    const wasOpen = this.dialog?.open;

    // 3) Inner card add: stay in the popup; confirm on the card button.
    if (wasOpen) {
      CS_LOG('inner card add -> update submit button, re-filter cards');
      const form = this.activeElement?.closest?.('product-form');
      const submitBtn = form?.querySelector?.('button[type="submit"]');
      const textSpan = submitBtn?.querySelector?.('.cs-card__submit-text');
      if (textSpan) {
        const added = window.variantStrings?.crossSellAdded?.trim() || '';
        if (added) textSpan.textContent = added;
      }
      if (submitBtn) {
        submitBtn.disabled = true;
        submitBtn.setAttribute('disabled', 'disabled');
        submitBtn.setAttribute('aria-disabled', 'true');
      }

      const eligible = this.applyEligibilityFilter();
      if (eligible === 0) {
        this.close();
        this.openCartDrawer();
      }
      return;
    }

    // 4) Initial PDP add: re-filter cards, then open popup or drawer.
    const eligible = this.applyEligibilityFilter();
    CS_LOG('eligible after refresh', eligible);

    if (eligible === 0) {
      CS_LOG('no eligible cards on initial open -> open cart-drawer instead');
      this.openCartDrawer();
    } else {
      this.open();
    }
  }

  getSectionInnerHTML(html, selector) {
    if (!html) return null;
    try {
      const sel = selector || '.shopify-section';
      const el = new DOMParser().parseFromString(html, 'text/html').querySelector(sel);
      return el ? el.innerHTML : null;
    } catch (e) {
      CS_LOG('getSectionInnerHTML failed', e);
      return null;
    }
  }

  /* ---------- Eligibility ---------- */

  async refreshCartState() {
    try {
      const res = await fetch('/cart.js', { credentials: 'same-origin', headers: { Accept: 'application/json' } });
      const cart = await res.json();
      this.cartVariantIds = new Set((cart.items || []).map((i) => i.variant_id));
      CS_LOG('cart variants from /cart.js', Array.from(this.cartVariantIds));
    } catch (e) {
      CS_LOG('refreshCartState failed', e);
    }
  }

  applyEligibilityFilter() {
    let eligible = 0;
    this.cards.forEach((card) => {
      const variants = card.__variants || [];
      const hasAvailable = variants.some((v) => v.available);
      const inCart = variants.some((v) => this.cartVariantIds.has(v.id));
      const show = hasAvailable && !inCart;
      card.hidden = !show;
      if (show) eligible += 1;
      CS_LOG('card eligibility', { productId: card.dataset.productId, hasAvailable, inCart, show });
    });
    return eligible;
  }

  /* ---------- Open / close ---------- */

  open() {
    if (!this.dialog || this.dialog.open) return;
    CS_LOG('open dialog');
    document.body.classList.add('overflow-hidden');
    this.dialog.showModal();
  }

  close() {
    if (!this.dialog || !this.dialog.open) return;
    CS_LOG('close dialog');
    this.dialog.close();
    document.body.classList.remove('overflow-hidden');
  }

  closeAndOpenCart() {
    this.close();
    // Tiny delay to let the dialog animation finish (not strictly required).
    setTimeout(() => this.openCartDrawer(), 200);
  }

  openCartDrawer() {
    const drawer = document.querySelector('cart-drawer');
    if (drawer && typeof drawer.open === 'function') {
      CS_LOG('opening cart-drawer (refreshing first)');
      this.refreshCartDrawer(drawer).finally(() => {
        drawer.open();
        if (typeof drawer.showAddedNotice === 'function') drawer.showAddedNotice(this.lastAddedTitle);
      });
    } else {
      CS_LOG('no <cart-drawer> on page; navigating to /cart');
      window.location.href = (window.routes && window.routes.cart_url) || '/cart';
    }
  }

  async refreshCartDrawer(drawer) {
    try {
      const sections = drawer.getSectionsToRender().map((s) => s.id);
      // Render the sections from the CURRENT page path, not /cart. The footer's
      // dynamic-checkout wallets are gated to `request.page_type != 'cart'` (so they
      // don't collide with the cart page's own once-per-page
      // `content_for_additional_checkout_buttons`). Fetching from /cart would make
      // page_type == 'cart' on every re-render and strip the wallets (they'd only
      // appear on the first page load). The current path keeps page_type correct.
      const base = window.location.pathname || (window.routes && window.routes.cart_url) || '/cart';
      const url = `${base}?sections=${encodeURIComponent(sections.join(','))}`;
      // NOTE: do NOT send `Accept: application/json` here. On the /cart path that
      // header makes Shopify return the cart object (token/items/total…) and the
      // `?sections=` param is ignored. The default Accept returns the Section Rendering
      // API JSON: `{ "cart-drawer": "<html>", "cart-icon-bubble": "<html>" }`.
      const res = await fetch(url, { credentials: 'same-origin' });
      const data = await res.json();
      drawer.getSectionsToRender().forEach((section) => {
        const target = section.selector ? document.querySelector(section.selector) : document.getElementById(section.id);
        const html = data[section.id];
        if (!target || !html) return;
        const sel = section.selector || '.shopify-section';
        const el = new DOMParser().parseFromString(html, 'text/html').querySelector(sel);
        if (el) target.innerHTML = el.innerHTML;
      });

      // The outer <cart-drawer> element lives OUTSIDE #CartDrawer, so the loop
      // above never updates its `is-empty` class. While it lingers, the CSS rule
      // `.is-empty .cart__contents { display: none }` hides the freshly injected
      // line items (visible only after a reload). Mirror the server's truth.
      const drawerHtml = data['cart-drawer'];
      if (drawerHtml) {
        const freshDrawer = new DOMParser().parseFromString(drawerHtml, 'text/html').querySelector('cart-drawer');
        if (freshDrawer) drawer.classList.toggle('is-empty', freshDrawer.classList.contains('is-empty'));
      }

      // The footer's dynamic-checkout wallets (content_for_additional_checkout_buttons →
      // <shopify-accelerated-checkout-cart>) were injected via innerHTML and Shopify does
      // NOT auto-hydrate section-rendered buttons — they stay blank until a reload. Re-init
      // them (same pattern as product-info.js / quick-add.js).
      window?.Shopify?.PaymentButton?.init();
    } catch (e) {
      CS_LOG('refreshCartDrawer failed', e);
    }
  }
}

if (!customElements.get('cross-sell-popup')) {
  customElements.define('cross-sell-popup', CrossSellPopup);
}
